<div class="space-y-6">
  <!-- ══════════ Account mode ══════════ -->
  <section hlmCard>
    <div hlmCardHeader>
      <h2 hlmCardTitle>Konto-Modus</h2>
      <p hlmCardDescription>Bestimmt, wie dein Konto in ein Netzwerk eingebunden ist.</p>
    </div>

    <div hlmCardContent class="flex flex-wrap items-center gap-3">
      @if (company?.is_group_admin) {
        <span hlmBadge variant="solid">Zentrale eines Netzwerks</span>
      } @else if (company?.is_member) {
        <span hlmBadge variant="secondary">
          Mitglied{{
            company?.member_of_company_name ? ' · ' + company?.member_of_company_name : ''
          }}
        </span>
      } @else {
        <span hlmBadge variant="outline">Eigenständig</span>
      }

      <a
        routerLink="/settings"
        [queryParams]="{ tab: 'verbund' }"
        class="text-sm underline underline-offset-4">
        Netzwerk-Einstellungen
      </a>
    </div>
  </section>

  <!-- ══════════ E-mail & password ══════════ -->
  <!-- The login stays with the customer: an agency working in the account can't change it -->
  @if (actingPartnerName()) {
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>E-Mail & Passwort</h2>
        <p hlmCardDescription>
          Die Zugangsdaten gehören deinem Kunden. Nur er kann E-Mail-Adresse, Passwort und
          Google-Anmeldung ändern.
        </p>
      </div>
    </section>
  } @else {
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>E-Mail & Passwort</h2>
        <p hlmCardDescription>Zugangsdaten für dein Konto.</p>
      </div>

      <div hlmCardContent class="space-y-6">
        <!-- ── Change e-mail ── -->
        <div class="space-y-3">
          <div>
            <h3 hlmSmall>E-Mail-Adresse ändern</h3>
            <p hlmMuted class="mt-1 text-sm">
              Aktuelle Adresse: <span class="text-foreground font-medium">{{ currentEmail }}</span>
            </p>
          </div>

          @if (emailChangePending) {
            <div hlmAlert>
              <ng-icon name="lucideMail" />
              <p hlmAlertTitle>Bestätigungslink gesendet</p>
              <p hlmAlertDescription>
                Wir haben einen Link an {{ pendingEmail }} geschickt. Klicke darauf, um die Änderung
                abzuschließen.
              </p>
            </div>
          }

          @if (!hasPassword) {
            <div hlmAlert>
              <ng-icon name="lucideInfo" />
              <p hlmAlertDescription>
                Du meldest dich mit Google an. Um deine E-Mail-Adresse zu ändern, lege zuerst unten
                ein Passwort fest.
              </p>
            </div>
          } @else {
            <form [formGroup]="emailForm" class="space-y-4">
              <div class="grid gap-4 sm:grid-cols-2">
                <div hlmField>
                  <label hlmFieldLabel for="new_email">Neue E-Mail-Adresse</label>
                  <input
                    hlmInput
                    id="new_email"
                    type="email"
                    formControlName="new_email"
                    placeholder="neue@adresse.de"
                    class="w-full"
                    [attr.aria-invalid]="isInvalid(emailForm, 'new_email') ? 'true' : null" />
                  @if (isInvalid(emailForm, 'new_email')) {
                    <p hlmFieldDescription class="text-destructive">
                      @if (emailForm.get('new_email')?.errors?.['required']) {
                        E-Mail-Adresse ist erforderlich
                      } @else {
                        Bitte gib eine gültige E-Mail-Adresse ein
                      }
                    </p>
                  }
                </div>

                <div hlmField>
                  <label hlmFieldLabel for="email_current_password">Aktuelles Passwort</label>
                  <hlm-input-group class="w-full">
                    <input
                      hlmInputGroupInput
                      id="email_current_password"
                      [type]="showEmailPassword ? 'text' : 'password'"
                      formControlName="current_password"
                      autocomplete="current-password" />
                    <hlm-input-group-addon align="inline-end">
                      <button
                        hlmInputGroupButton
                        type="button"
                        [attr.aria-label]="
                          showEmailPassword ? 'Passwort verbergen' : 'Passwort anzeigen'
                        "
                        (click)="showEmailPassword = !showEmailPassword">
                        <ng-icon [name]="showEmailPassword ? 'lucideEyeOff' : 'lucideEye'" />
                      </button>
                    </hlm-input-group-addon>
                  </hlm-input-group>
                  @if (isInvalid(emailForm, 'current_password')) {
                    <p hlmFieldDescription class="text-destructive">Passwort ist erforderlich</p>
                  }
                </div>
              </div>

              <div class="flex justify-end">
                <app-save-button
                  #emailSaveButton
                  size="sm"
                  text="E-Mail ändern"
                  [disabled]="!emailForm.valid"
                  [loading]="emailLoading"
                  (clickEvent)="onSubmitEmailChange()" />
              </div>
            </form>
          }
        </div>

        <div hlmSeparator></div>

        <!-- ── Password ── -->
        @if (hasPassword) {
          <div class="space-y-3">
            <div>
              <h3 hlmSmall>Passwort ändern</h3>
              <p hlmMuted class="mt-1 text-sm">Wähle ein sicheres, neues Passwort.</p>
            </div>

            <form [formGroup]="passwordForm" class="space-y-4">
              <div hlmField>
                <label hlmFieldLabel for="current_password">Aktuelles Passwort</label>
                <hlm-input-group class="w-full">
                  <input
                    hlmInputGroupInput
                    id="current_password"
                    [type]="showCurrentPassword ? 'text' : 'password'"
                    formControlName="current_password"
                    autocomplete="current-password" />
                  <hlm-input-group-addon align="inline-end">
                    <button
                      hlmInputGroupButton
                      type="button"
                      [attr.aria-label]="
                        showCurrentPassword ? 'Passwort verbergen' : 'Passwort anzeigen'
                      "
                      (click)="showCurrentPassword = !showCurrentPassword">
                      <ng-icon [name]="showCurrentPassword ? 'lucideEyeOff' : 'lucideEye'" />
                    </button>
                  </hlm-input-group-addon>
                </hlm-input-group>
                @if (isInvalid(passwordForm, 'current_password')) {
                  <p hlmFieldDescription class="text-destructive">Passwort ist erforderlich</p>
                }
              </div>

              <div hlmField>
                <label hlmFieldLabel for="new_password">Neues Passwort</label>
                <hlm-input-group class="w-full">
                  <input
                    hlmInputGroupInput
                    id="new_password"
                    [type]="showNewPassword ? 'text' : 'password'"
                    formControlName="new_password"
                    autocomplete="new-password" />
                  <hlm-input-group-addon align="inline-end">
                    <button
                      hlmInputGroupButton
                      type="button"
                      [attr.aria-label]="
                        showNewPassword ? 'Passwort verbergen' : 'Passwort anzeigen'
                      "
                      (click)="showNewPassword = !showNewPassword">
                      <ng-icon [name]="showNewPassword ? 'lucideEyeOff' : 'lucideEye'" />
                    </button>
                  </hlm-input-group-addon>
                </hlm-input-group>

                <ul class="mt-1 space-y-1">
                  <li
                    class="flex items-center gap-1.5 text-xs"
                    [class.text-muted-foreground]="!hasMinLength()"
                    [class.text-foreground]="hasMinLength()">
                    <ng-icon [name]="hasMinLength() ? 'lucideCheck' : 'lucideDot'" />
                    Mindestens 8 Zeichen
                  </li>
                  <li
                    class="flex items-center gap-1.5 text-xs"
                    [class.text-muted-foreground]="!(hasUppercase() && hasLowercase())"
                    [class.text-foreground]="hasUppercase() && hasLowercase()">
                    <ng-icon
                      [name]="hasUppercase() && hasLowercase() ? 'lucideCheck' : 'lucideDot'" />
                    Groß- und Kleinbuchstaben
                  </li>
                  <li
                    class="flex items-center gap-1.5 text-xs"
                    [class.text-muted-foreground]="!hasNumber()"
                    [class.text-foreground]="hasNumber()">
                    <ng-icon [name]="hasNumber() ? 'lucideCheck' : 'lucideDot'" />
                    Mindestens eine Zahl
                  </li>
                </ul>
              </div>

              <div hlmField>
                <label hlmFieldLabel for="confirm_password">Passwort bestätigen</label>
                <hlm-input-group class="w-full">
                  <input
                    hlmInputGroupInput
                    id="confirm_password"
                    [type]="showConfirmPassword ? 'text' : 'password'"
                    formControlName="confirm_password"
                    autocomplete="new-password" />
                  <hlm-input-group-addon align="inline-end">
                    <button
                      hlmInputGroupButton
                      type="button"
                      [attr.aria-label]="
                        showConfirmPassword ? 'Passwort verbergen' : 'Passwort anzeigen'
                      "
                      (click)="showConfirmPassword = !showConfirmPassword">
                      <ng-icon [name]="showConfirmPassword ? 'lucideEyeOff' : 'lucideEye'" />
                    </button>
                  </hlm-input-group-addon>
                </hlm-input-group>
                @if (passwordForm.get('confirm_password')?.value && !passwordsMatch()) {
                  <p hlmFieldDescription class="text-destructive">
                    Die Passwörter stimmen nicht überein
                  </p>
                }
              </div>

              <div class="flex justify-end">
                <app-save-button
                  #pwChangeSaveButton
                  size="sm"
                  text="Passwort ändern"
                  [disabled]="!passwordForm.valid || !passwordsMatch()"
                  [loading]="passwordLoading"
                  (clickEvent)="onSubmitPasswordChange()" />
              </div>
            </form>
          </div>
        } @else {
          <div class="space-y-3">
            <div>
              <h3 hlmSmall>Passwort festlegen</h3>
              <p hlmMuted class="mt-1 text-sm">
                Du meldest dich mit Google an und hast noch kein Passwort. Aus Sicherheitsgründen
                legst du dein erstes Passwort über einen Link fest, den wir dir per E-Mail senden.
              </p>
            </div>

            <button
              hlmBtn
              size="sm"
              type="button"
              [disabled]="setPasswordLoading || setPasswordEmailSent"
              (click)="onRequestSetPassword()">
              @if (setPasswordLoading) {
                <hlm-spinner />
                Wird gesendet…
              } @else {
                Passwort per E-Mail festlegen
              }
            </button>

            @if (setPasswordEmailSent) {
              <p hlmMuted class="text-sm">
                E-Mail wurde an {{ currentEmail }} gesendet. Folge dem Link, um dein Passwort
                festzulegen.
              </p>
            }
            @if (setPasswordError) {
              <p class="text-destructive text-sm">{{ setPasswordError }}</p>
            }
          </div>
        }

        <div hlmSeparator></div>

        <!-- ── Google link ── -->
        <div class="space-y-3">
          <h3 hlmSmall>Google-Konto</h3>

          @if (hasGoogle) {
            <div class="flex flex-wrap items-center gap-3">
              <span hlmBadge variant="solid">
                <ng-icon name="lucideCheck" />
                Mit Google verknüpft
              </span>
              <button
                hlmBtn
                variant="ghost"
                size="sm"
                type="button"
                [disabled]="!hasPassword || googleLinkLoading"
                (click)="onUnlinkGoogle()">
                Verknüpfung aufheben
              </button>
            </div>
            @if (!hasPassword) {
              <p hlmMuted class="text-xs">
                Lege zuerst ein Passwort fest, um die Verknüpfung aufheben zu können – sonst kannst
                du dich nicht mehr anmelden.
              </p>
            }
          } @else {
            <p hlmMuted class="text-sm">
              Verknüpfe dein Konto mit Google, um dich künftig mit einem Klick anzumelden. Die
              Google-E-Mail-Adresse muss nicht mit deiner Konto-E-Mail übereinstimmen.
            </p>
            <!-- Google Identity renders its button into this node -->
            <div #googleLinkBtn></div>
          }

          @if (googleLinkSuccess) {
            <p hlmMuted class="text-sm">{{ googleLinkSuccess }}</p>
          }
          @if (googleLinkError) {
            <p class="text-destructive text-sm">{{ googleLinkError }}</p>
          }
        </div>
      </div>
    </section>
  }

  <form [formGroup]="companyForm" class="space-y-6">
    <!-- ══════════ Contact ══════════ -->
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Kontakt</h2>
      </div>

      <div hlmCardContent class="grid gap-4 sm:grid-cols-2">
        <div hlmField>
          <label hlmFieldLabel for="first_name">Vorname</label>
          <input
            hlmInput
            id="first_name"
            type="text"
            formControlName="first_name"
            placeholder="Max"
            class="w-full"
            [attr.aria-invalid]="isInvalid(companyForm, 'first_name') ? 'true' : null" />
          @if (isInvalid(companyForm, 'first_name')) {
            <p hlmFieldDescription class="text-destructive">Vorname ist erforderlich</p>
          }
        </div>

        <div hlmField>
          <label hlmFieldLabel for="last_name">Nachname</label>
          <input
            hlmInput
            id="last_name"
            type="text"
            formControlName="last_name"
            placeholder="Mustermann"
            class="w-full"
            [attr.aria-invalid]="isInvalid(companyForm, 'last_name') ? 'true' : null" />
          @if (isInvalid(companyForm, 'last_name')) {
            <p hlmFieldDescription class="text-destructive">Nachname ist erforderlich</p>
          }
        </div>

        <div hlmField>
          <label hlmFieldLabel for="phone">Telefon</label>
          <input
            hlmInput
            id="phone"
            type="tel"
            inputmode="tel"
            formControlName="phone"
            placeholder="+49 170 1234567"
            class="w-full"
            [attr.aria-invalid]="isInvalid(companyForm, 'phone') ? 'true' : null" />
          @if (isInvalid(companyForm, 'phone')) {
            <p hlmFieldDescription class="text-destructive">Telefonnummer ist erforderlich</p>
          }
        </div>

        <div hlmField>
          <label hlmFieldLabel for="account_email">E-Mail</label>
          <input
            hlmInput
            id="account_email"
            type="email"
            [value]="currentEmail"
            readonly
            class="w-full" />
          <p hlmFieldDescription>
            {{
              actingPartnerName()
                ? 'Die Anmelde-Adresse ändert nur dein Kunde.'
                : 'Oben unter „E-Mail & Passwort“ änderbar.'
            }}
          </p>
        </div>

        <div hlmField class="sm:col-span-2">
          <label hlmFieldLabel for="website">Website</label>
          <input
            hlmInput
            id="website"
            type="url"
            formControlName="website"
            placeholder="mein-shop.de"
            class="w-full"
            [attr.aria-invalid]="isInvalid(companyForm, 'website') ? 'true' : null" />
          @if (isInvalid(companyForm, 'website')) {
            <p hlmFieldDescription class="text-destructive">
              Bitte gib eine gültige Website-URL ein
            </p>
          }
        </div>
      </div>

      <div hlmCardFooter class="justify-end border-t pt-4">
        <app-save-button
          #personalInfoSaveButton
          size="sm"
          [disabled]="!personalInfoChanged"
          [loading]="loading"
          (clickEvent)="onSubmitPersonalInfo()" />
      </div>
    </section>

    <!-- ══════════ Billing address ══════════ -->
    <section hlmCard>
      <div hlmCardHeader>
        <h2 hlmCardTitle>Rechnungsadresse</h2>
        <p hlmCardDescription>
          Diese Adresse wird für Rechnungen und offizielle Dokumente verwendet.
        </p>
      </div>

      <div hlmCardContent class="grid gap-4 sm:grid-cols-2">
        <div hlmField class="sm:col-span-2">
          <label hlmFieldLabel for="company_name">Firmenname</label>
          <input
            hlmInput
            id="company_name"
            type="text"
            formControlName="company_name"
            placeholder="Max Mustermann GmbH"
            class="w-full" />
        </div>

        <div hlmField class="sm:col-span-2">
          <label hlmFieldLabel for="vat_id">
            UST-ID / VAT ID{{ vatIdRequiredForCountry ? ' *' : '' }}
          </label>
          <input
            hlmInput
            id="vat_id"
            type="text"
            formControlName="vat_id"
            [placeholder]="vatIdPlaceholder"
            class="w-full" />
          <p hlmFieldDescription>{{ vatIdHint }}</p>
        </div>

        <div hlmField class="sm:col-span-2">
          <label hlmFieldLabel for="billing_street">Straße & Hausnummer *</label>
          <input
            hlmInput
            id="billing_street"
            type="text"
            formControlName="billing_street"
            placeholder="Musterstraße 123"
            class="w-full"
            [attr.aria-invalid]="isInvalid(companyForm, 'billing_street') ? 'true' : null" />
          @if (isInvalid(companyForm, 'billing_street')) {
            <p hlmFieldDescription class="text-destructive">Straße ist erforderlich</p>
          }
        </div>

        <div hlmField>
          <label hlmFieldLabel for="billing_city">Stadt *</label>
          <input
            hlmInput
            id="billing_city"
            type="text"
            formControlName="billing_city"
            placeholder="Musterstadt"
            class="w-full"
            [attr.aria-invalid]="isInvalid(companyForm, 'billing_city') ? 'true' : null" />
          @if (isInvalid(companyForm, 'billing_city')) {
            <p hlmFieldDescription class="text-destructive">Stadt ist erforderlich</p>
          }
        </div>

        <div hlmField>
          <label hlmFieldLabel for="billing_zip">Postleitzahl *</label>
          <input
            hlmInput
            id="billing_zip"
            type="text"
            inputmode="numeric"
            formControlName="billing_zip"
            placeholder="12345"
            class="w-full"
            [attr.aria-invalid]="isInvalid(companyForm, 'billing_zip') ? 'true' : null" />
          @if (isInvalid(companyForm, 'billing_zip')) {
            <p hlmFieldDescription class="text-destructive">Postleitzahl ist erforderlich</p>
          }
        </div>

        <div hlmField class="sm:col-span-2">
          <label hlmFieldLabel for="billing_country">Land *</label>
          <hlm-native-select
            selectId="billing_country"
            class="w-full"
            formControlName="billing_country"
            [disabled]="billingCountryLocked">
            @for (country of countries; track country.code) {
              <option hlmNativeSelectOption [value]="country.code">{{ country.name }}</option>
            }
          </hlm-native-select>
          @if (billingCountryLocked) {
            <p hlmFieldDescription>
              Das Land ist an dein Stripe-Konto gebunden und kann nachträglich nicht mehr geändert
              werden. Bitte wende dich an den Support.
            </p>
          }
        </div>
      </div>

      <div hlmCardFooter class="justify-end border-t pt-4">
        <app-save-button
          #billingAddressSaveButton
          size="sm"
          [disabled]="!billingAddressChanged"
          [loading]="loading"
          (clickEvent)="onSubmitBillingAddress()" />
      </div>
    </section>
  </form>
</div>
